import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/components/EventCard";
import type { LedgerEntry } from "@/components/Statement";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDate } from "@/lib/time";

export const savePdf = async (el: Element, filename: string, margin = 8) => {
  await (document as any).fonts?.ready;
  const html2pdf = (await import("html2pdf.js")).default as any;
  await html2pdf().set({ margin, filename, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "mm", format: "a4", orientation: "portrait" } }).from(el).save();
};

const ReceiptSheet = ({ e, name }: { e: LedgerEntry; name: string }) => {
  const { t, lang } = usePrefs();
  const cell = { padding: "10px 12px", borderBottom: "1px solid #eee" } as React.CSSProperties;
  const rows: [string, React.ReactNode][] = [
    [t("rc.no"), <bdi dir="ltr">{e.receipt_no}</bdi>], [t("rc.venue"), name],
    [t("b7.date"), fmtDate(e.effective_at || e.created_at, lang)],
    [t("b7.method"), e.method || "—"], [t("b7.reference"), e.reference || "—"],
  ];
  if (e.settlement) rows.push([t("b7.settled"), <bdi dir="ltr">{fmtMoney({ amount: e.settlement.amount, currency: e.settlement.currency } as any)} @ {e.settlement.rate} ({e.settlement.direction})</bdi>]);
  if (e.note) rows.push([t("b7.reason"), e.note]);
  rows.push([t("rc.by"), e.by]);
  return (
    <div dir={lang === "ar" ? "rtl" : "ltr"} style={{ width: 640, padding: 36, background: "#fff", color: "#111", fontFamily: "inherit", fontSize: 13 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "3px solid #111", paddingBottom: 12, marginBottom: 20 }}>
        <div><div style={{ fontSize: 22, fontWeight: 900 }}>{t("appName")}</div><div style={{ color: "#666" }}>{t("rc.title")}</div></div>
        <div style={{ textAlign: "end", fontWeight: 900, fontSize: 15 }} dir="ltr">{e.receipt_no}</div>
      </div>
      <div style={{ background: "#f4f4f4", borderRadius: 16, padding: "18px 20px", marginBottom: 20 }}>
        <div style={{ color: "#666" }}>{t("rc.received")}</div>
        <div style={{ fontSize: 28, fontWeight: 900 }}><bdi dir="ltr">{fmtMoney({ amount: e.amount, currency: e.currency } as any)}</bdi></div>
        {e.reversed_by && <div style={{ color: "#b45309", fontWeight: 700, marginTop: 4 }}>{t("rc.reversedNote")}</div>}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>{rows.map(([k, v]) => <tr key={k}><td style={{ ...cell, color: "#666", width: "38%" }}>{k}</td><td style={{ ...cell, fontWeight: 700 }}>{v}</td></tr>)}</tbody>
      </table>
      <p style={{ marginTop: 22, color: "#777", fontSize: 10 }}>{t("rc.footer")}</p>
    </div>
  );
};

export const ReceiptPdfButton = ({ e, name }: { e: LedgerEntry; name: string }) => {
  const { t } = usePrefs();
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const run = async () => {
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      await savePdf(ref.current!.firstElementChild!, `receipt-${e.receipt_no}.pdf`);
      toast.success(t("rc.downloaded"));
    } catch {
      toast.error(t("rc.failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={run} disabled={busy} data-testid={`receipt-pdf-${e.entry_id}`} className="h-8 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1 hover:bg-accent disabled:opacity-50">
        <FileDown className="h-3.5 w-3.5" />{busy ? t("common.loading") : t("rc.download")}
      </button>
      {busy && createPortal(<div ref={ref} style={{ position: "fixed", left: -10000, top: 0 }}><ReceiptSheet e={e} name={name} /></div>, document.body)}
    </>
  );
};
